import React, { useMemo, useState, useEffect, useRef } from 'react';
import {
  Box, Table, TableHead, TableBody, TableRow, TableCell, Typography, Button, InputBase,
} from '@mui/material';
import { SearchRounded, FileDownloadOutlined } from '@mui/icons-material';
import { colors } from '../theme/theme';
import { downloadCSV } from '../utils/format';

export interface Column<T> {
  key: string;
  label: string;
  align?: 'left' | 'right';
  /** Cell renderer. Defaults to row[key]. */
  render?: (row: T) => React.ReactNode;
  /** Value used for CSV export + built-in search. Defaults to row[key]. */
  csv?: (row: T) => string | number | null | undefined;
}

interface DataTableProps<T> {
  columns: Column<T>[];
  rows: T[];
  title?: string;
  /** Filename (without extension) — enables the Export Excel button. */
  exportName?: string;
  /** Show the built-in search box (filters across all column csv/key values). */
  search?: boolean;
  pageSize?: number;
  emptyText?: string;
}

const headSx = {
  py: 1.25, px: 1.75, bgcolor: colors.gray50, color: colors.textSecondary, fontSize: 11,
  fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.04em', whiteSpace: 'nowrap',
  borderBottom: `1px solid ${colors.border}`,
} as const;

const cellValue = <T,>(c: Column<T>, r: T) =>
  (c.csv ? c.csv(r) : (r as any)[c.key]) ?? '';

function DataTable<T>({ columns, rows, title, exportName, search, pageSize = 8, emptyText = 'No records.' }: DataTableProps<T>) {
  const [page, setPage] = useState(1);
  const [q, setQ] = useState('');

  const filtered = useMemo(() => {
    if (!q) return rows;
    const needle = q.toLowerCase();
    return rows.filter((r) => columns.some((c) => String(cellValue(c, r)).toLowerCase().includes(needle)));
  }, [rows, q, columns]);

  const total = filtered.length;

  // Reset to the first page only when the filtered result-set size changes (a real
  // search/filter was applied). An in-place data refresh that keeps the same number
  // of rows — e.g. toggling a row's status — leaves the user on their current page.
  const prevTotal = useRef(total);
  useEffect(() => {
    if (prevTotal.current !== total) { setPage(1); }
    prevTotal.current = total;
  }, [total]);
  const pages = Math.max(1, Math.ceil(total / pageSize));
  const current = Math.min(page, pages);
  const slice = filtered.slice((current - 1) * pageSize, current * pageSize);
  const from = total ? (current - 1) * pageSize + 1 : 0;
  const to = Math.min(current * pageSize, total);

  const exportCsv = () => {
    const cols = columns.filter((c) => c.label && c.key !== '_');
    downloadCSV(exportName || 'export', cols.map((c) => ({ label: c.label, value: (r: T) => cellValue(c, r) })), filtered);
  };

  const showBar = title || exportName || search;

  return (
    <Box>
      {showBar && (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, p: 2, borderBottom: `1px solid ${colors.border}`, flexWrap: 'wrap' }}>
          <Typography sx={{ fontWeight: 700, fontSize: 14, display: 'flex', gap: 1, alignItems: 'baseline' }}>
            {title}
            <Typography component="span" sx={{ color: colors.textSecondary, fontWeight: 500, fontSize: 12.5 }}>
              {total} record{total === 1 ? '' : 's'}
            </Typography>
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {search && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, border: `1px solid ${colors.border}`, borderRadius: '10px', px: 1.25, py: 0.5, minWidth: 200 }}>
                <SearchRounded sx={{ fontSize: 18, color: colors.textSecondary }} />
                <InputBase value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" sx={{ fontSize: 13, flex: 1 }} />
              </Box>
            )}
            {exportName && (
              <Button variant="outlined" size="small" startIcon={<FileDownloadOutlined />} onClick={exportCsv}
                sx={{ borderColor: colors.border, color: colors.textPrimary, '&:hover': { borderColor: colors.border, bgcolor: colors.gray50 } }}>
                Export Excel
              </Button>
            )}
          </Box>
        </Box>
      )}

      <Box sx={{ overflowX: 'auto' }}>
        <Table sx={{ '& td, & th': { fontSize: 13.5 } }}>
          <TableHead>
            <TableRow>
              {columns.map((c) => (
                <TableCell key={c.key} align={c.align || 'left'} sx={headSx}>{c.label}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {slice.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length} sx={{ border: 0 }}>
                  <Typography sx={{ textAlign: 'center', color: colors.textTertiary, py: 3.5, fontSize: 13.5 }}>{emptyText}</Typography>
                </TableCell>
              </TableRow>
            ) : (
              slice.map((r, i) => (
                <TableRow key={i} hover>
                  {columns.map((c) => (
                    <TableCell key={c.key} align={c.align || 'left'} sx={{ py: 1.5, px: 1.75, borderBottom: `1px solid ${colors.border}` }}>
                      {c.render ? c.render(r) : (r as any)[c.key]}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Box>

      {pages > 1 && (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, p: 2, borderTop: `1px solid ${colors.border}`, flexWrap: 'wrap' }}>
          <Typography sx={{ color: colors.textSecondary, fontSize: 12.5 }}>Showing {from}–{to} of {total}</Typography>
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <PagerBtn label="Prev" disabled={current === 1} onClick={() => setPage(current - 1)} />
            {Array.from({ length: pages }, (_, i) => i + 1).map((p) => (
              <PagerBtn key={p} label={String(p)} active={p === current} onClick={() => setPage(p)} />
            ))}
            <PagerBtn label="Next" disabled={current === pages} onClick={() => setPage(current + 1)} />
          </Box>
        </Box>
      )}
    </Box>
  );
}

const PagerBtn: React.FC<{ label: string; active?: boolean; disabled?: boolean; onClick: () => void }> = ({ label, active, disabled, onClick }) => (
  <Button
    onClick={onClick} disabled={disabled} disableElevation
    sx={{
      minWidth: 32, height: 32, px: 1, borderRadius: '8px', fontSize: 13, fontWeight: 600,
      border: `1px solid ${active ? colors.primary : colors.border}`,
      bgcolor: active ? colors.primary : '#fff', color: active ? '#fff' : colors.textSecondary,
      '&:hover': { bgcolor: active ? colors.primary : colors.gray50, borderColor: active ? colors.primary : colors.border },
    }}
  >
    {label}
  </Button>
);

export default DataTable;
